
import { Component, Vue, Prop, Emit } from 'vue-property-decorator';
import VTable from '@/components/shared/table/VTable.vue';
import { OptionGroup } from '@/interfaces/models/OptionGroup';
import { ColumnHeader } from '@/interfaces/components/ColumnHeader';
import headers from './headers';
import { Permission } from '@/enums/Permission';
import Article from '@/views/article/Article.vue';
import _ from 'lodash';
import i18n from '@/i18n';

@Component({
  components: { VTable },
})
export default class OptionTable extends Vue {
  @Prop({ type: Array, required: true }) public items!: OptionGroup[];
  @Prop({ type: Number, required: true }) public total!: number;

  public $refs!: {
    vTable: InstanceType<typeof VTable> & { vTable: any };
  };

  public deleteItemDialogVisible = false;
  public headers: ColumnHeader[] = headers;
  public maxArticle: number = 3;
  public searchOptions = {
    search: '',
    filterOptions: false,
  };

  public editDialog: any = {
    isVisible: false,
    value: null,
    name: '',
    original: null,
    cell: null,
  };

  public onSearchUpdate = _.debounce(() => {
    this.$refs.vTable.resetSelected();
    this.$emit('search', this.searchOptions);
    const query = Object.assign({}, this.$route.query);
    Object.assign(query, this.searchOptions);
    if (this.searchOptions.search && this.searchOptions.search.length > 0) {
      this.$router.replace({ query });
    } else {
      delete query.search;
      this.$router.replace({ query });
    }
  }, 500);

  public get tableItems(): OptionGroup[] {
    const options: OptionGroup[] = [...this.items];

    for (const option of options) {
      if (!option.hasOwnProperty('visible')) {
        option.visible = true;
      }
    }

    return options;
  }

  public get isFilterOption(): boolean {
    return this.searchOptions.filterOptions;
  }

  public created() {
    this.searchOptions.search = (this.$route.query.search as string) || '';
    this.searchOptions.filterOptions = this.$route.query.filterOptions === 'true' ? true : false;
  }

  public unselectAll(event: boolean) {
    if (!event) {
      this.$refs.vTable.resetSelected();
    }
  }

  public visibleArticles(articles: Article[]) {
    return articles.filter((article: Article, index: number) => index < this.maxArticle);
  }

  public edit(item: OptionGroup) {
    if (this.$can(Permission.OPTION_UPDATE)) {
      this.$router.push({ name: 'option.edit', params: { id: item._id }, query: { ...this.$route.query } });
    } else {
      this.$router.push({ name: 'option.editSimple', params: { id: item._id }, query: { ...this.$route.query } });
    }
  }

  public canToggleVisibility(val: boolean) {
    if (val) {
      return this.$can(Permission.OPTION_VISIBLE);
    }
    return this.$can(Permission.OPTION_HIDE);
  }

  public resetTable() {
    this.$refs.vTable.resetPagination();
  }

  public async deleteSelectable() {
    const ids = this.$refs.vTable.getSelected().map((option) => option._id);

    if (ids.length) {
      this.$emit('deleteGroup', ids);
      this.$refs.vTable.resetSelected();
    }
    this.deleteItemDialogVisible = false;
  }

  public handlePageEvent() {
    this.$refs.vTable.resetSelected();
  }

  public getEditDialogValue(headerValue: string, item: any): any {
    const localized = (value: string) => this.$options.filters!.localized(value);

    const valueHandlers: { [key: string]: (value: any) => any } = {
      name: localized,
      description: localized,
    };

    const handler = valueHandlers[headerValue];
    return handler ? handler(item[headerValue]) : item[headerValue];
  }

  public openEditCellModal(data: { header: ColumnHeader; item: any }) {
    if (!data.item || !Permission.OPTION_UPDATE) {
      return;
    }

    this.editDialog.isVisible = true;
    this.editDialog.name = data.header.value;
    this.editDialog.original = { ...data.item };
    this.editDialog.value = this.getEditDialogValue(data.header.value, data.item);
    this.editDialog.cell = data.header.value;
  }

  public saveCellValue() {
    const { name, value, original } = this.editDialog;

    const data = {
      original,
      field: name,
      value,
    };

    switch (name) {
      case 'name':
      case 'description':
        this.updateI18nField(data, name);
        break;
    }

    this.$emit('edited', data);
    this.stopEditing();
  }

  public updateI18nField(data: any, field: string) {
    const original = { ...this.editDialog.original[field] };
    original[i18n.locale] = this.editDialog.value;
    data.value = original;
  }

  public stopEditing() {
    this.editDialog.isVisible = false;
    this.editDialog.value = null;
    this.editDialog.original = null;
    this.editDialog.name = '';
  }

  @Emit('copy')
  public copyOption(id: string) {
    return id;
  }

  @Emit('handleOptionGroupCell')
  public handleOptionGroupCellEmit(item: OptionGroup) {
    return item;
  }
}
